import classNames from 'classnames';
import React__default, { useState, useRef, useEffect, useId } from 'react';
import { Label } from 'react-aria-components';
import { Button } from '../atoms/Button.js';
import { WarningIcon } from '../icons/WarningIcon.js';
import { ActionMenuTrigger, ActionMenu, ActionMenuItem } from './ActionMenu.js';
import './Dropdown.scss';

/**
 * A dropdown component that renders a button with the selected option text and opens an ActionMenu with other options on click.
 * Functionally similar to the HTML <select> element.
 *
 * ```tsx
 * // Uncontrolled usage
 * <Dropdown
 *   options={[
 *     { label: 'Option 1', value: 'option1' },
 *     { label: 'Option 2', value: 'option2' },
 *     { label: 'Option 3', value: 'option3', disabled: true },
 *     { label: 'Destructive Option', value: 'destructive', destructive: true },
 *     { label: 'With Description', value: 'with-desc', description: 'This option has a description' }
 *   ]}
 *   onSelectionChange={(value) => console.log('Selected:', value)}
 *   placeholder="Select an option"
 * />
 *
 * // With icon placeholder
 * <Dropdown
 *   options={options}
 *   onSelectionChange={(value) => console.log('Selected:', value)}
 *   placeholder={<SettingsIcon />}
 * />
 *
 * // Controlled usage
 * <Dropdown
 *   options={options}
 *   value={selectedValue}
 *   onSelectionChange={setSelectedValue}
 *   placeholder="Select an option"
 * />
 * ```
 */
const Dropdown = ({ options, value, onSelectionChange, placeholder = 'Select an option', disabled = false, className, buttonVariant = 'outline', size = 'large', label, isRequired = false, isClearable = false, hasError = false, name, autoComplete, }) => {
    const [internalValue, setInternalValue] = useState(null);
    const buttonRef = useRef(null);
    // Use controlled value if provided, otherwise use internal state
    const selectedValue = value !== undefined ? value : internalValue;
    const selectedOption = options.find((option) => option.id === selectedValue);
    // Use selectedLabel if provided, otherwise fall back to label
    const displayContent = selectedOption
        ? (selectedOption.selectedLabel ?? selectedOption.label)
        : placeholder;
    const handleSelectionChange = (keys) => {
        // React Aria returns a Set, so we need to get the first (and only) value
        const newValue = keys ? Array.from(keys)[0] : '';
        // Prevent deselection when clicking on an already selected item (unless isClearable is true)
        // When isClearable is false, users should only be able to change selection, not deselect
        if (!newValue && selectedValue && !isClearable) {
            // Ignore deselection attempts - don't update state or call onChange
            return;
        }
        // Only update internal state if not controlled
        if (value === undefined) {
            setInternalValue(newValue);
        }
        onSelectionChange?.(newValue);
    };
    useEffect(() => {
        if (value !== undefined) {
            setInternalValue(value);
        }
    }, [value]);
    const uniqueId = `rev-dropdown-${useId()}`;
    const showClearButton = isClearable && selectedValue?.trim() && !disabled;
    return (React__default.createElement("div", { "data-rev-component": 'dropdown', "data-has-error": !!hasError },
        autoComplete && (React__default.createElement("input", { className: 'dropdown-autofill-helper--rev', name: name ?? autoComplete, autoComplete: autoComplete, onChange: (e) => {
                const matchingOption = options.find((option) => option.id?.toLowerCase() === e.target.value.toLowerCase() ||
                    option.label?.toString().toLowerCase() ===
                        e.target.value.toLowerCase());
                if (matchingOption?.id) {
                    if (selectedValue === matchingOption.id) {
                        handleSelectionChange(null);
                    }
                    else {
                        handleSelectionChange(new Set([matchingOption.id]));
                    }
                }
            } })),
        label && (React__default.createElement(Label, { htmlFor: uniqueId, className: 'dropdown-label--rev', "data-rev-is-old-style": buttonVariant === 'old-style-dropdown' },
            label,
            isRequired && React__default.createElement("span", { className: 'field-label-required--rev' }, "(required)"))),
        React__default.createElement(ActionMenuTrigger, null,
            React__default.createElement(Button, { ref: buttonRef, id: uniqueId, variant: buttonVariant /* Temporary casting to support old-style variant */, isDisabled: disabled, size: size, icon: showClearButton ? (React__default.createElement("div", { className: 'dropdown-chevron--rev' })) : (React__default.createElement("div", { className: 'dropdown-chevron--rev' },
                    React__default.createElement("svg", { xmlns: 'http://www.w3.org/2000/svg', width: '10', height: '6', viewBox: '0 0 10 6', fill: 'none' },
                        React__default.createElement("path", { d: 'M9.3224 0C9.9275 0.000280795 10.2299 0.780695 9.7931 1.24219L5.48744 5.78711C5.21834 6.07101 4.7809 6.07109 4.51185 5.78711L0.207163 1.24219C-0.230103 0.780649 0.0724189 0 0.677867 0H9.3224Z', fill: 'currentColor', fillOpacity: '0.8' })))), iconSide: 'right', className: classNames('dropdown-button--rev', className) },
                React__default.createElement("span", { className: 'dropdown-button-text--rev' }, displayContent)),
            React__default.createElement(ActionMenu, { placement: 'bottom start', selectionMode: 'single', selectedKeys: selectedValue ? new Set([selectedValue]) : new Set(), onSelectionChange: handleSelectionChange }, options.map((option, index) => (React__default.createElement(ActionMenuItem, { key: index, label: option.label, description: option.description, disabled: option.disabled, destructive: option.destructive, className: option.className, leadingAccessory: option.leadingAccessory, trailingAccessory: option.trailingAccessory, id: option.id }))))),
        hasError && typeof hasError === 'string' && (React__default.createElement("span", { className: 'dropdown-error--rev' },
            React__default.createElement(WarningIcon, null),
            hasError)),
        showClearButton && (React__default.createElement(Button, { className: 'dropdown-clear-button--rev', variant: 'ghost', size: 'tiny', rounding: '8', ariaLabel: 'Clear', icon: React__default.createElement(XIcon, null), onPress: () => handleSelectionChange(null) }))));
};
function XIcon() {
    return (React__default.createElement("svg", { xmlns: 'http://www.w3.org/2000/svg', width: '8', height: '8', viewBox: '0 0 8 8', fill: 'none' },
        React__default.createElement("path", { d: 'M6.21967 0.219666C6.51256 -0.0732274 6.98732 -0.0732276 7.28021 0.219666C7.57299 0.512568 7.57307 0.987357 7.28021 1.28021L4.81049 3.74994L7.28021 6.21967C7.57299 6.51257 7.57307 6.98736 7.28021 7.28021C6.98736 7.57307 6.51257 7.57299 6.21967 7.28021L3.74994 4.81049L1.28021 7.28021C0.987357 7.57307 0.512568 7.57299 0.219666 7.28021C-0.0732275 6.98732 -0.0732274 6.51256 0.219666 6.21967L2.68939 3.74994L0.219666 1.28021C-0.0732272 0.987319 -0.0732275 0.512559 0.219666 0.219666C0.512559 -0.0732269 0.987319 -0.073227 1.28021 0.219666L3.74994 2.68939L6.21967 0.219666Z', fill: '#333333' })));
}

export { Dropdown };
//# sourceMappingURL=Dropdown.js.map
